<script>
    import { onMount } from "svelte";

    import PatientDocuments from "./PatientDocuments.svelte";

    import {
        CardLayout,
        RichTextEditor,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import { editIcon } from "../../../Utils/utilConstants.svelte";

    import { getText, showError } from "../../../Utils/utilFunctions.svelte";

    export let patientDetail = {},
        syncPatientFiles = () => {};

    $: btnOptions[1].isHidden = isInEdit;

    const btnOptions = [
        {
            iconName: "addIcon",
            hasTooltip: false,
            tooltip: "Upload document",
            iFont: "fa-solid fa-upload",
            iconFunction: () => _openUploadFileModal(),
            btnText: "Upload document",
        },
        {
            iconName: "editNoteIcon",
            iFont: editIcon,
            iconFunction: () => _toggleEditMode(),
            hasTooltip: false,
            btnText: "Edit",
        },
    ];

    let isInEdit = false,
        backupPatientDetails = {},
        isUpdatingNotes = false;

    let isUploadFileModalOpen = false;

    onMount(() => {
        updateBackupDetails();
    });

    const updateNotes = async () => {
        isUpdatingNotes = true;

        await updatePatientDetails();

        await syncPatientFiles();

        updateBackupDetails();

        isUpdatingNotes = false;

        _toggleEditMode();
    };

    const updatePatientDetails = async () => {
        await apiMethods.putMethod
            .updatePatient(patientDetail)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update patient details, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) => showError(err));
    };

    const discardChanges = () => {
        _toggleEditMode();
        patientDetail = {
            ...patientDetail,
            badDebtor: backupPatientDetails.badDebtor,
            badDebtorByMedicare: backupPatientDetails.badDebtorByMedicare,
            notes: backupPatientDetails.notes,
            alerts: backupPatientDetails.alerts,
        };
    };

    const updateBackupDetails = () => {
        backupPatientDetails = { ...patientDetail };
    };

    const handleReloadAfterFileChanges = async () => {
        if (!isInEdit) await syncPatientFiles();
        else {
            updateBackupDetails();

            await syncPatientFiles();

            let tempPatientNotes = patientDetail.notes;
            let tempPatientAlerts = patientDetail.alerts;

            if (
                backupPatientDetails.notes != tempPatientNotes ||
                backupPatientDetails.alerts != tempPatientAlerts
            ) {
                patientDetail.notes = backupPatientDetails.notes;
                patientDetail.alerts = backupPatientDetails.alerts;
                backupPatientDetails.notes = tempPatientNotes;
                backupPatientDetails.alerts = tempPatientAlerts;
            }
        }
    };

    // HELPER FUNCTIONS
    const _toggleEditMode = () => (isInEdit = !isInEdit);

    const _openUploadFileModal = () => (isUploadFileModalOpen = true);
</script>

<CardLayout title="Alerts and Documents" {btnOptions}>
    <div class="flex flex-col flex-1 items-center gap-4 px-2 py-4 w-full">
        <div class="flex flex-1 gap-x-12 w-full">
            <div class="flex gap-x-4">
                <label for="deceased" class="cursor-pointer"> Deceased </label>
                <input
                    type="checkbox"
                    id="deceased"
                    class="toggle toggle-secondary"
                    disabled={!isInEdit}
                    bind:checked={patientDetail.deceased}
                />
            </div>

            <div class="flex gap-x-4">
                <label for="badDebtor" class="cursor-pointer">
                    Bad Debtor
                </label>
                <input
                    type="checkbox"
                    id="badDebtor"
                    class="toggle toggle-secondary"
                    disabled={!isInEdit}
                    bind:checked={patientDetail.badDebtor}
                />
            </div>

            {#if backupPatientDetails.badDebtorByMedicare}
                <div class="flex flex-1 gap-x-4">
                    <label
                        for="badDebtorByMedicare"
                        class="title cursor-pointer"
                    >
                        Bad Debtor recognised by Medicare Number
                    </label>
                    <input
                        type="checkbox"
                        id="badDebtorByMedicare"
                        class="toggle toggle-secondary"
                        disabled={!isInEdit}
                        bind:checked={patientDetail.badDebtorByMedicare}
                    />
                </div>
            {/if}
        </div>

        <!-- <div class="flex flex-col flex-1 w-full gap-y-2">
            <p class="title font-bold underline">Patient Notes:</p>
            {#if isInEdit}
                <textarea
                    class="textarea textarea-bordered w-full"
                    rows="4"
                    bind:value={patientDetail.notes}
                />
            {:else}
                <div
                    class="flex flex-1 w-full border border-2 rounded-lg px-2 py-1 leading-8 whitespace-pre-line min-h-8"
                >
                    {getText(patientDetail.notes)}
                </div>
            {/if}
        </div> -->

        <div class="flex flex-col flex-1 w-full gap-y-2">
            <p class="title text-error font-bold underline">Patient Alerts:</p>
            <!-- {#if isInEdit}
                <textarea
                    class="textarea textarea-bordered w-full"
                    rows="4"
                    bind:value={patientDetail.alerts}
                />
            {:else}
                <div
                    class="flex flex-1 w-full border border-2 rounded-lg px-2 py-1 leading-8 whitespace-pre-line min-h-8"
                >
                    {getText(patientDetail.alerts)}
                </div>
            {/if} -->
            <RichTextEditor
                bind:content={patientDetail.alerts}
                config={{ isDisabled: !isInEdit }}
            />
        </div>

        <div class="flex flex-col flex-1 w-full gap-y-2">
            <p class="title font-bold">Patient Documents:</p>

            <div
                class="flex flex-1 w-full border border-2 rounded-lg px-2 py-1 leading-8 min-h-8"
            >
                <PatientDocuments
                    bind:isUploadFileModalOpen
                    patientDetails={patientDetail}
                    syncPatientFiles={handleReloadAfterFileChanges}
                />
            </div>
        </div>

        {#if isInEdit}
            <div class="flex flex-1 w-full items-center justify-end gap-x-4">
                <button
                    class="btn btn-sm btn-error w-30"
                    disabled={isUpdatingNotes}
                    on:click|preventDefault={discardChanges}
                >
                    Cancel
                </button>
                <button
                    class="btn btn-sm btn-secondary w-30 {isUpdatingNotes
                        ? 'loading'
                        : ''}"
                    disabled={isUpdatingNotes}
                    on:click|preventDefault={updateNotes}
                >
                    {isUpdatingNotes ? "Saving" : "Save"}
                </button>
            </div>
        {/if}
    </div>
</CardLayout>

<style>
    .title {
        width: 200px;
    }
</style>
